import { useEffect, useState } from 'react'
import { ArrowRight, Calculator, Loader2, Save } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useMaterial, useSaveMaterial } from '@/hooks/useInventoryMaster'
import { computeReorder, formatReorderNumber, reorderParamsFrom, toReorderNumber } from '@/lib/reorder'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { ReorderCalculatorPanel, type ReorderCalcField } from './ReorderCalculatorPanel'
import { CreatePurchaseOrderDialog } from './purchase-orders/CreatePurchaseOrderDialog'

interface ReorderDialogProps {
  /** The material's database id; null closes the dialog. */
  materialId: string | null
  onClose: () => void
}

/** The four settings the calculation runs on, which "Save settings" writes back. */
const SETTINGS: ReorderCalcField[] = ['dailyConsumption', 'orderLeadTimeDays', 'releaseTimeDays', 'safetyStockDays']

/**
 * Step 1 of re-ordering a material (z8nrz7f7dz): the calculation, before any
 * purchase order. The planner checks or adjusts the material's figures
 * (optionally saving them back), sees the decision and suggested quantity, and
 * settles the quantity to order. Step 2, Create purchase order, opens with
 * that quantity on the line; the PO form itself carries no calculation.
 */
export function ReorderDialog({ materialId, onClose }: ReorderDialogProps) {
  const { data: material, isLoading } = useMaterial(materialId)
  // What the planner has typed over the stored values. Saved only on request.
  const [typed, setTyped] = useState<Partial<Record<ReorderCalcField, string>>>({})
  // The quantity to order, once the planner overrides the suggestion.
  const [qty, setQty] = useState<string | null>(null)
  const [creating, setCreating] = useState<number | null>(null)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = useSaveMaterial()
  const { can } = usePermission()
  const canCreate = can(PERMISSIONS.purchaseOrdersManage)
  const canSave = can(PERMISSIONS.inventoryManage)

  useEffect(() => {
    setTyped({})
    setQty(null)
    setCreating(null)
    setSaved(false)
    setError(null)
  }, [materialId])

  const attrs = (material?.attributes as Record<string, { value?: unknown }> | undefined) ?? {}
  const value = (key: string): unknown => (key in typed ? typed[key as ReorderCalcField] : attrs[key]?.value)
  // Live stock, never typed: the decision is made against what is on the shelf.
  const onHand = material?.totalQuantity ?? 0
  const stock = { onHand, available: material?.availableQuantity ?? onHand, onOrder: material?.reorder?.onOrder ?? 0 }
  const onOrder = stock.onOrder
  const result = computeReorder(reorderParamsFrom(value), stock)
  const orderQty = qty ?? String(result.suggestedQty)
  const orderNum = toReorderNumber(orderQty) ?? 0
  const settingsChanged = SETTINGS.some((k) => k in typed)

  function saveSettings() {
    if (!material) return
    setError(null)
    const attributes = Object.fromEntries(
      SETTINGS.filter((k) => k in typed).map((k) => [k, { active: true, value: toReorderNumber(typed[k]) }]),
    )
    save.mutate(
      { id: material.id, payload: { attributes } },
      {
        onSuccess: () => {
          setTyped({})
          setSaved(true)
          window.setTimeout(() => setSaved(false), 2500)
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'The settings could not be saved.'),
      },
    )
  }

  return (
    <>
      <Dialog open={materialId !== null && creating === null} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className='max-h-[92vh] overflow-y-auto sm:max-w-2xl'>
          <DialogHeader className='space-y-1'>
            <DialogTitle className='flex items-center gap-2'>
              <Calculator className='h-5 w-5 text-primary' />
              Re-order calculation
            </DialogTitle>
            {material && (
              <DialogDescription>
                <span className='font-mono'>{material.materialId}</span> · {material.materialName}
                {onOrder > 0 && <> · {formatReorderNumber(onOrder)} already on order</>}
              </DialogDescription>
            )}
          </DialogHeader>

          {isLoading || !material ? (
            <div className='flex items-center gap-2 py-8 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading material…
            </div>
          ) : (
            <div className='space-y-4'>
              <ReorderCalculatorPanel
                get={value}
                stock={stock}
                onChange={(key, raw) => setTyped((p) => ({ ...p, [key]: raw }))}
              />

              {canSave && (settingsChanged || saved) && (
                <div className='flex flex-wrap items-center gap-2'>
                  <Button size='sm' variant='outline' className='gap-1' onClick={saveSettings} disabled={save.isPending || !settingsChanged}>
                    <Save className='h-4 w-4' />
                    {save.isPending ? 'Saving…' : 'Save settings to material'}
                  </Button>
                  {saved ? (
                    <span className='text-xs font-medium text-green-600'>✓ Saved</span>
                  ) : (
                    <span className='text-xs text-slate-500'>Your changes are a what-if until saved.</span>
                  )}
                </div>
              )}
              {error && <p className='text-sm text-destructive'>{error}</p>}

              <div className='flex flex-wrap items-end justify-between gap-3 border-t pt-4'>
                <div className='space-y-1'>
                  <Label htmlFor='reorder-order-qty' className='text-xs font-medium text-slate-600'>
                    Quantity to order
                  </Label>
                  <div className='flex items-center gap-2'>
                    <Input
                      id='reorder-order-qty'
                      type='number'
                      min={0}
                      step='any'
                      inputMode='decimal'
                      className='h-9 w-32 tabular-nums'
                      value={orderQty}
                      onChange={(e) => setQty(e.target.value)}
                    />
                    {qty !== null && Number(qty) !== result.suggestedQty && (
                      <button type='button' className='text-xs font-medium text-primary hover:underline' onClick={() => setQty(null)}>
                        Use suggested ({formatReorderNumber(result.suggestedQty)})
                      </button>
                    )}
                  </div>
                </div>
                <div className='flex gap-2'>
                  <Button variant='outline' onClick={onClose}>
                    Close
                  </Button>
                  {canCreate && (
                    <Button className='gap-1' onClick={() => setCreating(orderNum)} disabled={orderNum <= 0}>
                      Create purchase order
                      <ArrowRight className='h-4 w-4' />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CreatePurchaseOrderDialog
        materialId={creating !== null ? materialId : null}
        qty={creating ?? undefined}
        onClose={() => {
          setCreating(null)
          onClose()
        }}
      />
    </>
  )
}
